import React from 'react';
import { Home, ChevronRight, Volume2, ArrowLeft, Mail, Heart, Shield, Compass, Plane } from 'lucide-react';
import { ArchivalImageCard } from '../ArchivalImageCard';
import { useVoiceReader } from '../../context/VoiceReaderContext';

interface IsabelDentonSectionProps {
  onSelectPart?: (partId: string) => void;
}

export const IsabelDentonSection: React.FC<IsabelDentonSectionProps> = ({ onSelectPart }) => {
  const { speakCurrentPage, isPlaying } = useVoiceReader();

  return (
    <div className="space-y-10 font-sans text-[#2c221e]">
      {/* Sub-navigation Breadcrumbs & Voice Narrator Controls */}
      <header className="bg-[#f3ede0] p-6 sm:p-8 md:p-10 rounded-3xl border-2 border-[#d8ceb9] shadow-sm space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3 text-xs font-serif text-[#7a5b3a]">
          {/* Breadcrumb: The Dentons > Part 9 of 9: Isabel & Warren Denton */}
          <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1.5 sm:gap-2">
            <button
              onClick={() => onSelectPart && onSelectPart('hub')}
              className="hover:underline flex items-center gap-1 font-bold text-[#7a5b3a]"
            >
              <Home className="w-3.5 h-3.5" />
              <span>The Dentons</span>
            </button>
            <ChevronRight className="w-3.5 h-3.5 text-[#d8ceb9]" />
            <span className="bg-[#e8dec9] px-2.5 py-1 rounded-full font-bold uppercase tracking-wider text-[#7a5b3a] text-[11px] sm:text-xs">
              Part 9 of 9: Isabel &amp; Warren Denton
            </span>
          </nav>

          {/* Audio Narration Trigger */}
          <button
            onClick={speakCurrentPage}
            className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-[#7a5b3a] hover:bg-[#5c4328] text-white text-xs font-bold transition shadow-xs cursor-pointer"
            title="Listen to Isabel & Warren Denton"
          >
            <Volume2 className={`w-3.5 h-3.5 ${isPlaying ? 'animate-pulse text-amber-200' : 'text-amber-300'}`} />
            <span>{isPlaying ? 'Listening to Section...' : 'Listen to this Chapter'}</span>
          </button>
        </div>

        <h1 className="font-serif text-3xl md:text-5xl font-extrabold text-[#2c221e] tracking-tight leading-tight pt-1">
          ISABEL DENTON – THE GIRL WHO JOINED THE LAND ARMY
        </h1>

        <div className="flex items-center gap-2 text-xs font-serif text-[#7a5b3a] uppercase tracking-wider font-semibold">
          <Heart className="w-3.5 h-3.5" />
          <span>Part 9 of the 9-Part Denton Family History</span>
        </div>
      </header>

      {/* Main Archival Text Content */}
      <article className="bg-[#fbf9f4] p-6 sm:p-10 md:p-12 rounded-3xl border border-[#d8ceb9] shadow-sm space-y-10 text-base md:text-lg leading-relaxed text-[#2c221e]">
        
        {/* Section 1: Isabel Denton Introduction */}
        <section className="space-y-6">
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#2c221e]">
            ISABEL DENTON – THE GIRL WHO JOINED THE LAND ARMY
          </h2>

          {/* Image 124: Heather, Margaret, Isabel and Harry in RAF uniform */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/124 Heather, Margaret, Isabel and Harry in RAF uniform.jpg"
              alt="Heather, Margaret, Isabel and Harry in RAF uniform"
              caption="Heather, Margaret, Isabel and Harry in his RAF uniform"
              filenameHint="124 Heather, Margaret, Isabel and Harry in RAF uniform.jpg"
            />
          </div>

          {/* Image 125: Isabel Denton */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/125 Isabel Denton.png"
              alt="Isabel Denton"
              caption="Isabel Denton"
              filenameHint="125 Isabel Denton.png"
            />
          </div>

          <p>
            Isabel Denton (1921 – 1973) was 18 when the Second World War began. She wanted to join other
            women and join the forces especially after her cousin Meryl Cole had left her job as a shop assistant
            to join the Women’s Auxillary Army Corps.
          </p>

          <p>
            Meryl was in Jayforce, which had women manning coastal and anti-aircraft defences, providing
            catering, clerical, entertaining, medical and signalling support, and serving in the Pacific. Her parents,
            Fred and Helen Cole of Knowehead near Waiau did not need her to assist on the farm.
          </p>

          <p>
            Isabel’s situation was quite different. Her younger sister Heather said that it was with some
            reluctance that Isabel realised she would have to work on the farm rather than joining the services,
            which is what she wanted to do very much indeed.
          </p>

          <p>
            Isabel’s older brother Harry had joined the Royal New Zealand Airforce at Woodbourne. He went to
            England where he was posted to Bomber Command and joined No 9 Squadron of the Royal Air
            Force, flying Lancasters. He would later be awarded the Distinguished Flying Cross for his efforts
            landing his plane after it was shot, coming down in a field near Heesch in The Netherlands.
          </p>

          <p>
            Her younger brother Warren served in the New Zealand before transferring to the Air Force Defence
            Group ground crew at Tauranga where he studied engineering and later serviced Harvard engines at
            Woodbourne.
          </p>

          <p>
            There was a general shortage of men to do the essential annual round of farm work such as
            shearing, cultivation, harvesting, haymaking and fencing on the family farm <em>Oakdale</em>, a 450-ha sheep
            and cattle property on Feredays Road, near Southbridge.
          </p>

          <p>
            By joining the land girls Isabel got formal recognition for her contribution and that she got paid. The
            government set the wages and Heather recalled that her father said he’d never had to pay anyone
            so much! Heather remembered well that Isabel always made everything fun, that she was full of life
            and just lovely to be around. Getting well paid by her father was also a source of some amusement,
            and pride.
          </p>

          <p>
            The land girls did make a contribution and helped provide the food both NZ and the UK needed. NZ
            was later also expected to provide food for the 100,000 American servicemen stationed throughout
            the Pacific Islands. The food priorities were meat, dairy products, wheat, honey, eggs, seeds,
            potatoes, vegetables, flax and wool.
          </p>

          {/* Image 126 & 127: Poster advertising for land girls & Isabel on Oakdale horse */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8">
            <ArchivalImageCard
              src="/images/126 Poster advertising for land girls.jpg"
              alt="Poster advertising for land girls"
              caption="Poster advertising for land girls"
              filenameHint="126 Poster advertising for land girls.jpg"
            />
            <ArchivalImageCard
              src="/images/127 Isabel on Oakdale horse.jpg"
              alt="Isabel on Oakdale horse"
              caption="Isabel on Oakdale horse"
              filenameHint="127 Isabel on Oakdale horse.jpg"
            />
          </div>

          <p>
            The NZ Government had followed the example of the UK government which had established a Land
            Girls programme but had not been as successful. Recruitment posters proclaimed “Help Farm for
            Victory – Good wages, smart uniform, full working kit” but the UK posters were far more
            motivational.
          </p>

          <p>
            In the first six months the NZ Women’s Land Service – the land girls - only attracted 203 recruits. In
            September 1942 the scheme was extended to cover women working on farms belonging to relatives
            and that led to about 4500 women putting on the chocolate brown uniform and getting paid.
          </p>

          <p>
            After four weeks, the land girls were given their uniform: sou-westers, overalls, gumboots, leggings,
            woollen socks, raincoat and a smart dress uniform in chocolate brown.
          </p>

          <p>
            “Oh yes, she wore her uniform gear all the time. When she went to town she wore the dress
            uniform, I remember that brown uniform. She wore it when she went to things like the A &amp; P Shows
            too,” Heather (90) recalled in 2021.
          </p>

          <p>
            The Women’s Land Service is recorded in a book by Dianne Bardsley “The Land Girls: In a Man’s
            World 1939-46.”
          </p>

          <p>
            A memorial bronze plaque and seat dedicated to the land girls is on the Rongomaraeroa Peace
            Walk, 143 Timaunga Road, Maungati, opposite the Maungati Church Hall, south of Timaru.
          </p>

          {/* Image 128 & 129: Plaque for NZ Women_s Land Service & Isabel and Warren Denton */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8">
            <ArchivalImageCard
              src="/images/128 Plaque for NZ Women_s Land Service at Maungati.jpg"
              alt="Plaque for NZ Women's Land Service at Maungati"
              caption="Plaque for NZ Women's Land Service at Maungati"
              filenameHint="128 Plaque for NZ Women_s Land Service at Maungati.jpg"
            />
            <ArchivalImageCard
              src="/images/129 Isabel and Warrn Denton.png"
              alt="Isabel and Warren Denton"
              caption="Isabel & Warren in their service uniforms"
              filenameHint="129 Isabel and Warrn Denton.png"
            />
          </div>

          <p>
            For some people though, this was a step too far. A letter to the Women’s Weekly in 1940 read:
            “Women who can cut the throats of sheep and carry heavy loads will not make healthy future
            mothers of the race.”
          </p>

          <p>
            Warren procured a small RNZAF pennant for Isabel and it was made into a pendant. The gold key
            was a 21st birthday gift from her parents.<sup>1</sup>
          </p>

          {/* Footnote 1 */}
          <div className="p-4 rounded-xl bg-[#e8dec9]/60 border border-[#d8ceb9] text-xs text-[#4a3525] italic my-6">
            <p><sup>1</sup> The pendant and locket are in the possession of Shirley Shiel, Isabel’s eldest daughter.</p>
          </div>
        </section>

        {/* LETTERS FROM ISABEL TO WARREN */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#2c221e]">
            LETTERS FROM ISABEL TO WARREN
          </h2>

          {/* Letter 1 */}
          <div className="space-y-4 bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] shadow-xs">
            <div className="text-xs font-serif font-bold text-[#7a5b3a] uppercase tracking-wider space-y-0.5">
              <p>Home</p>
              <p>Dec 3rd, 1942</p>
            </div>
            <div className="font-serif text-base sm:text-lg italic text-[#2c221e] space-y-4 leading-relaxed">
              <p>Dear old Cherub</p>
              <p>
                How's the works love, it is raining here, has been all day, no go at all. George is in a state, he cut his
                lucerne hay and then went on shearing, now he has got shivering sheep and with long leaps, trying
                to do two things at once as per usual.
              </p>
              <p>
                Dad cut our hay in the orchard, and it is ruined so he will put it around the trees. Cecil had stacked
                all his except 2 lorry loads, so his bit is a gonner too. We are going flat out to get you an Air Force
                jumper knitted for Xmas, it is in heavy weight wool in the dark A.F. colour, you will like it, so hope to
                have it done.
              </p>
              <p>
                Margaret went through to town with the Robert’s yesterday in the car, she had 4 teeth filled at the
                London dental, she is terribly tired and hasn't walked more than a yard all day, she seems worse
                than ever.
              </p>
              <p>
                My dear wee Dicky chicks are growing. There are two now and the biggest one is 11 days old now,
                and is just getting his feathers, he is going to be blue so is sure to be a rooster. Dicky is cheeky as
                ever and Danny keeps giving him a hiding.
              </p>
              <p>
                Lester Warren and I are quite good friends now, he's a nice kid and talks horses all the time, or dogs,
                most shepherds do. Last Saturday it rained, and Dad and I went out all afternoon with a crook ewe
                and as soon as we got her home she died, we have her lamb in, he was just born.
              </p>
              <p>
                I went to the dance at night, only a few there, about 20 couples. I had a good enough time only Andy
                is getting to be a real pest, five dances running is no go with me, he must cut out or else I cut him
                out.
              </p>
              <p>
                Morris P. was there, he is in the camp at Ashburton now on A.D.V. and Keith B. Is the same at
                Nelson. Morris asked after you and was most interested. Andy asked me to go to the Bible class
                social last night, said he would fetch me home so I wouldn't go.
              </p>
              <p>
                They took Murry G. To the doctor tonight, his left leg is smaller and shorter, you can see it at a
                glance. Mum has sold another ferret, that's two just in a week. The man is coming out here to ferret
                any day soon.
              </p>
              <p>
                I cut my hair off short again, it was down to my shoulders and too much bother. The shearers aren’t
                here yet and don't look if they will be here for a week or two, but the weather is no good anyhow.
              </p>
              <p>
                I had a letter from Owen this week and he didn’t say much, poor Dear, don't expect he feels like
                writing at all.
              </p>
              <p>
                Our garden looks great, and Laughton says we can get our Christmas peas out of his paddock.
              </p>
              <p className="not-italic pt-2">
                Lots of love<br />
                Isabel
              </p>
            </div>
          </div>

          {/* Letter 2 */}
          <div className="space-y-4 bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] shadow-xs">
            <div className="font-serif text-base sm:text-lg italic text-[#2c221e] space-y-4 leading-relaxed">
              <p>Dear Warren</p>
              <p>
                How's the measle spots? When anyone asks after you and I say you are in hospital with measles,
                they laugh and think it's funny that you should have them, don't suppose you think it is a joke at all.
              </p>
              <p>
                These last two days have been great, light frost’s and then the sun, only a rather cold wind, do hope
                we don't get any more rain. Have just muzzled the phone, Helen Bishop trying to ring Joe Conrad,
                she still does two or three times a week.
              </p>
              <p>
                Our battery has just come back from being charged, so we got the Kiwi concert tonight. The wireless
                is going just as good as ever, and Dad wouldn't be without it now for the war news, which seems a
                lot better now. Mum and Heather are getting ready to go to town tomorrow, Heather to the dentist
                and mum some shopping. Last Saturday afternoon I went down to vote and then stayed at Carr’s for
                tea. Heather came down after tea, so Mrs Carr came to the pictures with us, it was George Formby
                in “ It’s Turned out Nice again”. I had heard that it was one of his poor pictures, but didn't think so,
                thoroughly enjoyed it.
              </p>
              <p>
                Mr. Carr has been on a trip up to Nelson and had thought of buying a house up there and selling out
                down here, he can’t now with this Govt. land bill. His back is still very bad. Mrs. Carr was very
                grateful for a dress I gave wee Pamela, so I have just finished three pairs of pyjamas for Frankie.
              </p>
              <p>
                Say, if you ever see a card of hairclips in any of the shops up there, be a dear and get them for me, I
                haven't any left. I went over to the Robert’s on Sunday, Mrs. Robert’s seemed very upset by her
                brother's death, she told me all the sad story and I nearly howled instead of being sympathetic.
              </p>
              <p>
                Last night I went out with the rifle after a rabbit, got about halfway up Gilbert’s plantation and found
                one of his ewe’s in trouble. I got the lamb O.K a big beauty and had a job getting him to breath.
                Stayed there for an hour and he seemed alright, went home and rang them up, they should have
                gone and got the lamb and ewe, but they went out visiting and this morning he was dead. I saved his
                life, and they didn't care enough to bring him in. Our lambing is practically over, I am putting all our
                sheep on green feed every morning and taking them off at night. We still have five pet lambs but
                there may be a chance of getting rid of some of them yet. I gave Mertle one for the kiddies and
                Winnie has one.
              </p>
              <p>
                Is there any chance of your getting home to convalesce? There are no dances on here, the one this
                week was put off because of the show, flower show, old daffodils, and things.
              </p>
              <p>
                Well, I must go to bed jolly tired I am.
              </p>
              <p className="not-italic pt-2">
                Cheerio old Love<br />
                Isabel xxxxxxx
              </p>
              <p className="not-italic text-sm text-[#4a3525]">
                PS. How’s Jim, are you and he still together.
              </p>
            </div>
          </div>

          {/* Letter 3 */}
          <div className="space-y-4 bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] shadow-xs">
            <div className="text-xs font-serif font-bold text-[#7a5b3a] uppercase tracking-wider space-y-0.5">
              <p>Friday night</p>
              <p>Sep. 24th 1943</p>
            </div>
            <div className="font-serif text-base sm:text-lg italic text-[#2c221e] space-y-4 leading-relaxed">
              <p>Dear Warren</p>
              <p>
                Your letter came yesterday you poor dear, I am sorry measles are nasty, now don't start scratching
                or you may well be worse. Two letters came from Harry today, one I have sent up to Margaret with
                instructions to send on without delay, the other I will enclose.
              </p>
              <p>
                It is raining here again. Dad went to Southbridge this morning to collect chickens off the bus, so I
                rode round the sheep after dinner, only 80 or 90 left to lamb now. Tomorrow we go to vote, hope it
                is fine as Heather and I want to go and see the George family film at night.
              </p>
              <p>
                Melva Kidd is talking of getting married next Easter to Clarry White, a returned soldier of this war,
                and has asked me to be one of her bridesmaids, I will. She also asked after you Harry, they haven't
                heard from Tom for quite a while.
              </p>
              <p>
                I will write again soon
              </p>
              <p className="not-italic pt-2">
                Love<br />
                Isabel
              </p>
            </div>
          </div>

          {/* Image 130: Isabel_s letter to Warren */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/130 Isabel_s letter to Warren.jpg"
              alt="Isabel's letter to Warren"
              caption="Isabel's letter to Warren"
              filenameHint="130 Isabel_s letter to Warren.jpg"
            />
          </div>

          {/* Letter 4 */}
          <div className="space-y-4 bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] shadow-xs">
            <div className="text-xs font-serif font-bold text-[#7a5b3a] uppercase tracking-wider space-y-0.5">
              <p>Thursday</p>
              <p>July 29th 1943</p>
            </div>
            <div className="font-serif text-base sm:text-lg italic text-[#2c221e] space-y-4 leading-relaxed">
              <p>Dear Warren</p>
              <p>
                I went to town yesterday and then to Maurice Pilkington's send off at night, then stayed the rest of
                the night at Carr’s. It seems hard Maurice having to go, but he is keen and will be going to Canada
                for sure. When he gets his notice to report, and if he has to come to the North Island to do so, he
                will ring me, and I will send you a telegram so that you will have a chance of seeing him. You could
                say he was your brother and get leave to meet the boat. Let me know as soon as you get this, how
                things are and what would be best to put in a telegram to convince the officials.
              </p>
              <p>
                Keith B. is up at Rotorua and is very disappointed at not going also, he has something wrong with
                him, don't know what. I have to go now and put the sheep off the green feed. Margaret went to
                town on Tuesday and is coming home tonight, she wouldn't come with me last night, so now I have
                to go and meet her.
              </p>
              <p>
                What is your number can't find it anywhere? How's the nose? Do you want some nice biscuits, if so
                send back the tin and I will refill with marshmallow shortcake, it's good.
              </p>
              <p className="not-italic pt-2">
                Cheerio Love<br />
                Isabel
              </p>
            </div>
          </div>
        </section>

        {/* AFTER THE WAR */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <p>
            <strong>AFTER THE WAR</strong> Isabel married Henry Montgomery from Southbridge on 6 February 1946, and they
            farmed at Raincliff and later at Dunsandel. Sadly, she died on 24 May 1973, leaving behind her
            children Roger, Noel, Shirley, Lynley, Robert and the late William.
          </p>

          {/* Image 131: Isabel_s wedding 6 february 1946 with Annie and Tommy Denton */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/131 Isabel_s wedding 6 february 1946 with Annie and Tommy Denton.jpg"
              alt="Isabel's wedding 6 February 1946 with Annie and Tommy Denton"
              caption="With Annie & Tommy Denton on her wedding day 6 February 1946"
              filenameHint="131 Isabel_s wedding 6 february 1946 with Annie and Tommy Denton.jpg"
            />
          </div>
        </section>

        {/* HENRY LUCAS MONTGOMERY (HARRY) */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#2c221e]">
            HENRY LUCAS MONTGOMERY (HARRY) WAS A SHEARER BEFORE HE LEFT FOR WAR
          </h2>

          <p>
            From the peaceful fertile arable cropping plains of Southbridge north of the Rakaia River to
            some of the fiercest battles of World War Two, two Montgomery cousins were really in the
            thick of things.
          </p>

          <p>
            Robert Gilpin Montgomery<sup>2</sup>, (1921 – 14 March 1944) was killed on active service in Italy and
            is buried in the Cassino War Cemetery (below), about 120 km south east of Rome. He had
            181 pounds in a saving account which he left to his mother, along with everything he
            owned.
          </p>

          <p>
            Harry told his daughter in law Shirley that he was standing talking to his cousin Gilpin Montgomery
            (22) when a sniper’s bullet killed him.
          </p>

          <p>
            Harry did return home, married Isabel Denton, had six children, and lived a peaceful life marred by
            the death of an infant son in 1957 and Isabel’s early death in 1973.
          </p>

          <p>
            Like so many servicemen he hardly ever talked of his war experiences. He did tell Shirley than when
            he was offered the chance of joining the Tank Division he took it, saying that he was sick of walking
            so driving a tank had a lot of appeal. However, of his original crew of five he was the only one to
            survive.
          </p>

          <p>
            That was after Harry was one of the NZ men who took part in what has become known as the bold
            and daring break through at Minqar Qa’im on the night of 27-28 June 1942.
          </p>

          <p>
            The NZ’ers had been fighting in the sand of North Africa with such loses that the NZ Government
            insisted they be withdrawn to Syria to recover. They did not get very long to recuperate as the
            German Commander Rommel launched a fresh offensive in the Western Desert and the Kiwis were
            recalled to North Africa.
          </p>

          <p>
            German forces cut the Kiwis off from the Allied Forces and they were soon surrounded. They had
            one day’s ammunition left so they undertook a bold plan, under the Commander of the 2nd NZ
            Expeditionary Force, Major General Bernard ‘Tiny’ Freyberg. They would take the Germans by
            surprise, and at midnight they started their famed break through, using bayonets and rifles and their
            trucks they broke through. Peter McIntryre’s famous painting of the breakthrough captures some of
            the drama Harry would have experienced being right there.
          </p>

          {/* Image 132: Henry Montgomery */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/132 Henry Montgomery.png"
              alt="Henry Montgomery"
              caption="Henry Lucas Montgomery- from Southbridge to the sandy deserts of North Africa (Photos: Lynley Edwards)"
              filenameHint="132 Henry Montgomery.png"
            />
          </div>

          <p>
            After being in the infantry and later a tank driver, Harry worked as a Prisoner of War Guard after the
            German surrender. He said the only Germans he hated were the Nazis and that” the rank and file
            were just ordinary men wondering how the hell they had gone from being shopkeepers teacher and
            farmers to being in the German Army trying to kill other men.”
          </p>

          {/* Footnote 2 */}
          <div className="p-4 rounded-xl bg-[#e8dec9]/60 border border-[#d8ceb9] text-xs text-[#4a3525] italic my-6">
            <p>
              <sup>2</sup> Son of David and Mary (nee Hannaford) Montgomery of Southbridge. Mary’s parents were George (born in
              Windecombe in the Moor, Devon) and Mary Gilpin, born in Cornwall.
            </p>
          </div>

          <p>
            Harry did not display his medals and he never took part in Anzac Day services.
          </p>

          <hr className="border-t-2 border-[#d8ceb9] my-6" />

          <p>
            Harry was the son of William James Montgomery (1889 -1978) and Bertha Wise Reesby (1896 –
            1974). He was named after his Irish-born grandfather, Henry Lucas Montgomery who was born at
            Ballynahatty who married Mary Eleanor Bates, from Omagh (Ireland) in 1875 at Omagh. This Irish
            couple came to Southbridge in central Canterbury.
          </p>

          <p>
            Harry’s mother Bertha (1896 – 1974) was born at Ellesmere, the daughter of a couple who came out
            from England. Her father was Sam Thomas Reesby from Crick, in Northamptonshire, a lovely
            landscape of rolling hills and stone villages. Her mother was Emma Wise from Saddleworth in the
            beautiful Peak District of north west England.
          </p>

          {/* Image 133: Southbridge High Street */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/133 Southbridge High Street.jpg"
              alt="Southbridge High Street"
              caption="Southbridge High Street in its early days"
              filenameHint="133 Southbridge High Street.jpg"
            />
          </div>
        </section>

        {/* WARREN DENTON */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#2c221e]">
            WARREN DENTON (1923 – 1980)
          </h2>
          <p className="font-serif text-xl font-semibold text-[#7a5b3a]">
            Ground crew to soaring thermals
          </p>

          <p>
            He had a huge hearty laugh, a lovely way with children, so many ideas and the practical engineering
            skills to implement innovations, the inspiration and commitment to try new things and achieve
            them. For years he drove a grey truck with “J T Denton &amp; Son Ltd” written on the side door.
          </p>

          <p>
            Warren spent most of his life on <em>Oakdale</em> except for the war years. He worked on local farms during
            the harvest and shearing when he was younger.
          </p>

          <p>
            He was in the NZ Army for a short time then transferred to the Air Force Defence Crew at Tauranga.
            He studied engineering and later serviced Havard engines at Woodbourne. It was a huge
            disappointment to him that he could not serve overseas because of his asthma, but the engineering
            knowledge he gained drove inventions and adaptions he made to machines and systems for the rest
            of his life.
          </p>

          <p>
            Warren got his Private Pilot’s Licence, then a Glider Towing Licence in 1956. He had a share in the
            German designed high performance Weihe Glider ZK GAE, in which he recorded NZ’s first recorded
            100+ km triangle flight.
          </p>

          <p>
            This is from <em>Soaring NZ</em> magazine, Issue 57, May – July 2019, page 52 in the section “Blast from the
            Past”.
          </p>

          <blockquote className="p-6 bg-[#f3ede0] rounded-2xl border-l-4 border-[#7a5b3a] font-serif italic text-base sm:text-lg text-[#2c221e] shadow-xs space-y-4">
            <p>
              “January 1959 and the first ever 100 km triangle was flown by Warren Denton in the Weihe GAE. He
              flew from Burnt Hill to Burnham to Highbank Power Station and back to Burnt Hill, a total distance of
              113 km.
            </p>
            <p>
              Using thermals, which topped out at 5,000 feet, he made good time until the Highbank to Burnt Hill
              leg. Flying into a stiff easterly he was at times down to 600 feet and at one stage nearly had to land
              in the middle of a cricket match on the Glentunnel Domain. There was no golf course next door then.
              Note that this leg took 1 hour 10 minutes of the total flying time of 3 hours 6 minutes. Being a
              farmer, Warren had a good laugh when he saw a farmer drilling a paddock and steering a very
              crooked course watching the low flying glider.
            </p>
            <p>
              Another interesting flight Warren did about then was sparing the Waimakariri River bank near the
              Gorge and for about a mile downstream. Flying just a couple of hundred feet above the bank, using
              easterly life, he soared for quite a while, at times startling sightseers near the high gorge bridge by
              calling out to them as he turned above their heads. To land, a very quick turn was made into a
              paddock on top of the terrace.”
            </p>
          </blockquote>

          {/* Image 134: Warren Denton with ZK GAE in 1959 */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/134 Warren Denton with ZK GAE in 1959.jpg"
              alt="Warren Denton with ZK GAE in 1959"
              caption="Warren with ZK GAE in 1959 – “Aviator, Inventor” is on his headstone"
              filenameHint="134 Warren Denton with ZK GAE in 1959.jpg"
            />
          </div>
        </section>

        {/* LETTERS FROM ANNIE TO WARREN DURING WW2 */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#2c221e]">
            LETTERS FROM ANNIE TO WARREN DURING WW2
          </h2>

          {/* Annie Letter 1 */}
          <div className="space-y-4 bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] shadow-xs">
            <div className="text-xs font-serif font-bold text-[#7a5b3a] uppercase tracking-wider space-y-0.5">
              <p>Friday June 11th</p>
            </div>
            <div className="font-serif text-base sm:text-lg italic text-[#2c221e] space-y-4 leading-relaxed">
              <p>Dear Warren</p>
              <p>
                These letters all came Wed. If you like you can post them to Margaret at Waiau. She may stay on
                sometime, another three weeks anyway. Harry must be like all the rest looking for letters. I have
                sent 6 letters and Isabel has written once, and I have a long letter nearly finished.
              </p>
              <p>
                Dad and I had a great day last Wed. We drove to catch the morning train lucky it wasn’t a cold
                morning at 3 o’clock. We had a family party at the Co-op tearooms for Uncle Stewart’s and Aunt
                Jennie’s golden wedding. It was in the small room off the main tearoom. Grandma was there, the
                first time she had been out for 2 years. All her family and some of her in-laws, all Uncle and Aunt’s
                children except Greta, the one in the N.I. and most of their grandchildren.
              </p>
              <p>
                Aunt Mary, who I have not seen for 21 years, and she didn’t know me either, said she didn’t think
                anyone could change so much. The time was not long enough to have a talk to any one person.
                Morris came and Helen I don’t know if they spoke, but Edna came just as the party was breaking up
                and I took her to speak to him.
              </p>
              <p>
                We had to hurry for our train. They had a proper wedding cake and all. Eion Gibb and his wife were
                there, and they are expecting a family in Nov. Owen Foster the boy from the place Morris works at is
                missing.
              </p>
              <p>
                We have 27 more rows of potatoes to dig, not a heavy crop. Your yarn about the yanks was hot.
                Isabel says, she will take it down for L. Tong to read. He has 2 sacks of barley crushed for me, so I
                want to get them this afternoon. The pigs use too much of my hen wheat, but one is ready to kill.
                The barley will finish off the other.
              </p>
              <p>
                Aunt Helen is having some more yanks to stay with her, and only Margaret to help her. Edna’s baby
                still in hospital and she is staying in town. Merle is settled in the Army, she had a heavy transport
                licence at the store so she could drive the store van, so she is on as a heavy transport driver in the
                Army.
              </p>
              <p>
                I felt stiff and now have had a little rest writing this. I must go and get some dinner ready. There is a
                cold wind and Isabel has been out since breakfast.
              </p>
              <p className="not-italic pt-2">
                Dear old Warren, best love<sup>3</sup>
              </p>
            </div>
          </div>

          {/* Annie Letter 2 */}
          <div className="space-y-4 bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] shadow-xs">
            <div className="text-xs font-serif font-bold text-[#7a5b3a] uppercase tracking-wider space-y-0.5">
              <p>Oakdale</p>
              <p>Tuesday June 15th</p>
            </div>
            <div className="font-serif text-base sm:text-lg italic text-[#2c221e] space-y-4 leading-relaxed">
              <p>Dear Warren</p>
              <p>
                Two more letters came from Harry on Friday, so I enclose them. You could send them on to
                Margaret, Box 26, Waiau. She seems a lot better and not wanting to come home, I think she would
                write and say if she did.
              </p>
              <p>
                Eric Moses has just rung up from Oxford, says he is feeling fairly well, asked after you, in fact thought
                you might be home. We have no word about you getting off, so suppose it has not been looked into
                really, they don’t intend to let you go. The next thing they will be sending you off to some of the
                Island’s I suppose.
              </p>
              <p>
                On Sat. morning Mr Ballagh rang up and asked Dad and I to go to Wigram and see the big Lancaster
                land. Isabel drove us out to the main road, and we met the Ballaugh’s there. I don’t know what put it
                into their heads to ask us. It was a lovely day, and a sight worth seeing. On Sunday morning the
                Lancaster flew over Southbridge and then came here, it was quite low and went between Gilbert’s
                trees and here, Dad said about 600 feet up, it looked huge. It was nearly dark when we got home but
                Isabel had all the outside jobs done.
              </p>
              <p>
                Only eight rows of potatoes to do now, tomorrow will finish the job if it doesn’t rain, it nearly did
                today. It is not the crop like we had last year but quite fair all the same. This afternoon C. Gilbert
                brought Litty and her foal over to our yard and Dad tied the foal up, it did go to market. In about an
                hour it had got quite tame, and they led it home. They mean to get it in again soon, it is a pretty little
                chap.
              </p>
              <p>
                Mrs Bryce heard from Derby last night. He says the war won’t be over for two years as far as he can
                see. Isabel had a letter on Sat. from Eric Harris, the chap Harry brought home from Ashburton, the
                first we had heard from him since he was here. He has done two hundred flying hours and is to be at
                Norwood now for a short time.
              </p>
              <p>
                Well old dear you will be 20 on Saturday, so we all send “Many Happy Returns” I hope a small cake
                will follow in about a week, I couldn’t get the things to make a proper one, so waited and can get
                them about Thursday. I intend to make up a parcel to send Harry soon, as by his letters it seems he
                finds the food a bit queer. I have kept two young roosters just in case you were home for your
                birthday. It looks as if the Christmas Goose will be ready by the time you get here.
              </p>
              <p className="not-italic pt-2">
                Love from home<br />
                Mum
              </p>
            </div>
          </div>

          {/* Footnote 3 */}
          <div className="p-4 rounded-xl bg-[#e8dec9]/60 border border-[#d8ceb9] text-xs text-[#4a3525] italic my-6">
            <p><sup>3</sup> From his mother, Annie Denton</p>
          </div>

          {/* Annie Letter 3 */}
          <div className="space-y-4 bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] shadow-xs">
            <div className="text-xs font-serif font-bold text-[#7a5b3a] uppercase tracking-wider space-y-0.5">
              <p>Sunday 7th March</p>
            </div>
            <div className="font-serif text-base sm:text-lg italic text-[#2c221e] space-y-4 leading-relaxed">
              <p>Dear Warren</p>
              <p>
                I will write now so when your Rongotai address comes I can post it at once.
              </p>
              <p>
                Harry came home last Wed. night. He went to ChCh by train and had his bike with him. Margaret
                went to Southbridge on Wed afternoon and stayed with the Carr’s. She had some flowers to fix up
                for the Show on Thursday. She did 9 entries and got 3 prizes. Elva Hampton also had 8 entries and
                she and Margaret got a bit peeved about their treatment. Harry, Heather and I drove down early in
                the evening and Harry and I walked round and spent about ½ hour at Mrs B. Hampton’s, then went
                to the show.
              </p>
              <p>
                Margaret was just about beat when we got home, but after a day’s rest she seemed alright. The last
                time she went to the Doctor he changed her medicine. I think she is slowly getting better but can’t
                stand much. Harry and Isabel went to town Friday morning and on to Waiau Friday afternoon by bus.
                I think they will come home Tuesday. The R.S.A are having a dance next Thursday at S.B. and Harry
                and C. Hickman are getting their farewell then. I don’t know what they are going to give them. As
                Harry expects to go by ferry to Wellington and on by train to Auckland to get the boat to Canada, he
                may see you. You are getting nearer home, you might get a run down before long.
              </p>
              <p>
                Isabel gave up going to visit Marcia as she was afraid of being away when Harry came home, so has
                missed her chance to visit you in Rotorua. I know you will be disappointed being grounded, but after
                all it may lead to something you will find you like quite well.
              </p>
              <p>
                Harry says Rongotai is not very well liked. His mate Eric very nearly got sent there, he got very air
                sick, and didn’t land a plane very well. Now he is at Harewood.
              </p>
              <p>
                The annual business meeting of the Women’s Institute was held last Thursday, so I biked down to
                Horn’s and went on with them and joined the Institute. They had a hot meeting, Mrs Horne and I sat
                quiet and enjoyed it. Mrs Ballagh has been President and has not pleased everyone by any means.
              </p>
              <p>
                When Isabel and I went to town we hunted everywhere, but no skirt to be found. I told Mrs Taylor
                but she couldn’t get one. She said she has heard that the Para Rubber Coy had some, we never
                thought of going there, and of course they would be all gone before we could get near them. Isabel
                could make one if she could only get some material. How did you get on with hay making?
              </p>
            </div>
          </div>

          {/* Annie Letter 4 */}
          <div className="space-y-4 bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] shadow-xs">
            <div className="text-xs font-serif font-bold text-[#7a5b3a] uppercase tracking-wider space-y-0.5">
              <p>March 15</p>
            </div>
            <div className="font-serif text-base sm:text-lg italic text-[#2c221e] space-y-4 leading-relaxed">
              <p>
                Your letter arrived, now I must get this away as it will be sometime since you have had a letter. I will
                use those sugar coupons, it all adds to my reserve. I did not make much jam but want to make some
                quince and might get a case of late peaches.
              </p>
              <p>
                We had another hailstorm last Wed. and it did more mischief in the garden than the one a week
                before, apples and pears about ruined.
              </p>
              <p>
                The W.I. (Women’s Institute) have a dance and cards on tonight and Dad, Isabel and Harry went to it,
                got a ride in C. Gilbert’s car. Margaret went also to stop with the Carr babies. Mrs Carr likes to get
                out as Mr Carr will be playing. He gets a good few nights out with the band now, about all the poor
                chap can do, but he does try to do odd jobs. Dad gave him all the saws to do.
              </p>
              <p>
                Tonight, Isabel got a short letter from Marcia and quite a plump envelope in it for Harry to be sent
                on if he had left. Each day we are looking out for the notice for Harry to go, but no word so far. We
                will send you a wire if time is short when it does.
              </p>
              <p>
                People around the District have taken some American Marines for a few days. Dr Hunt has 3, Mrs
                Graham 2, Mrs Beamley 1, Mrs Ballagh 2 and so on, 19 all counted I think. They are to be at this
                dance tonight.
              </p>
              <p>
                Harry and C. Hickman got on quite well with their speeches last Thursday night. Harry did not seem
                nervous at all. He said he very much regretted that you were not with him to meet so many friends,
                and that went over well “hear, hear” and “good old Warren” came from all around.
              </p>
              <p>
                The football club gave him a leather folder with writing materials in it and the district gave him the
                usual £1.10
              </p>
              <p>
                Harry’s rabbit traps were in the shed and they have disappeared. We think they went the night of
                the send-off. Those Grey’s are camped in the hut up at the concrete shed. Smith came up Sunday
                and he and Harry went up and saw them but no sign of the traps. Some of the Grey gang are
                trapping up at Coalgate so perhaps they sent the traps there. But it was a dirty trick when they knew
                we would all be away. Reasby is trapping too, and he has got old Terry, if he likes him he will keep
                him. Billy Monty is out in the riverbed also, and they are all getting big catches.
              </p>
              <p>
                Your photo’s came last night and we all love them, you have such a nice grin. There must have been
                a girl there, you shouldn’t wink love, no go.
              </p>
              <p>
                We went down to the dance last night (Monday 15th). It was for the American Marines. There were
                about 20 there and Alice and I had some fun with them, they really are a decent lot and their talk
                isn’t so bad at all. They taught us how to do the real jitter-bug and also some of their dances.
              </p>
              <p>
                Tonight, Winnie and I are going to Leeston and if Harry isn’t too tired he will come too. I hope Marg
                looked after Mrs Carr’s babies last night and will be going to Leeston with Mr Carr tonight, he has to
                play. (letter ended)
              </p>
            </div>
          </div>

          {/* Annie Letter 5 */}
          <div className="space-y-4 bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] shadow-xs">
            <div className="text-xs font-serif font-bold text-[#7a5b3a] uppercase tracking-wider space-y-0.5">
              <p>Thursday Sep 23rd</p>
            </div>
            <div className="font-serif text-base sm:text-lg italic text-[#2c221e] space-y-4 leading-relaxed">
              <p>Dear Warren</p>
              <p>
                You have had a time. I expect you just felt really ill. It takes a while to get over measles, funny how
                you escaped getting them when we had them and then caught them now.
              </p>
              <p>
                Isabel got a letter from Melva Kidd today, asking her to be a bridesmaid for her wedding next Easter,
                making arrangements in plenty of time. There have not been any letters from Harry lately. The war
                looks better every day, you wouldn’t think it could last a great while longer.
              </p>
              <p>
                Mrs Tong rang up tonight to see if I had any chickens big enough to cook as Arthur is in bed with
                pleurisy and Laughton bad with ‘flu’. I have 3 big black roosters hatched in June, so can let her have
                them. The eggs haven’t hatched at all well so far, but I hope for better luck in a few weeks’ time.
              </p>
              <p>
                The geese have done no good at all. One time you wrote you said how Harry doing so well made you
                feel you hadn’t done much good. Nobody thinks like that it was your disappointment and not your
                fault. Everyone asks just as much after you as they do after Harry. How would you like to be Allan
                Wiley? Been a camp cook for 3 years, (gee) you don’t want to ask him if he likes it. Merle Cole maybe
                home on leave soon, the little cat. She is having a tremendous time, more boys making dates than
                she can keep count of it seems.
              </p>
              <p>
                Isabel gets tired and sick of her job, and then Merle writes about her fine doings and Isabel gets the
                blues, or the willies, or something. Did Isabel tell you about this boy Eric Tucker who stayed here
                from Monday till Saturday a few weeks back? He has come out and taken her to two dances. I don’t
                like him much, something very weak about him. If you had been home, you would soon found out
                what he really is. I think Isabel has just about had enough of him anyhow.
              </p>
              <p>
                It has rained on and off so long that the grass is coming to soft now. Dad has only got in one half day
                farrowing the paddock. All teamwork has been held up. R. Bishop was talking to Dad today and told
                him Freddy cut the tops off 2 fingers in the grain crusher a week ago, so no war for Freddy now.
              </p>
              <p>
                Gilbert’s have a boy working for them. I think he is a little childish and just has to be told everything
                to do, but they like him. He has a dreadful scar on his neck where he was scolded when 3 years old.
              </p>
              <p>
                Murray goes to school every day now. Heather says he just walks and runs as queer as ever he did.
                The Gilbert’s say he is completely cured. Dad says he doesn’t think there is anything to pay in the
                way of taxes on your money.
              </p>
              <p>
                We have to go to vote on Sat – well there isn’t much excitement about that. A lot of people think
                Labour will get a shift. The cherry plums all flowered in the rain so there won’t be much fruit on
                them. We can’t get into the garden so all vegetables will be late. My Autumn sown seeds did very
                little good, the peas looked waterlogged only the broad beans are in flower and some spinach fit to
                cook.
              </p>
            </div>
          </div>

          {/* Annie Letter 6 */}
          <div className="space-y-4 bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] shadow-xs">
            <div className="text-xs font-serif font-bold text-[#7a5b3a] uppercase tracking-wider space-y-0.5">
              <p>Tuesday</p>
            </div>
            <div className="font-serif text-base sm:text-lg italic text-[#2c221e] space-y-4 leading-relaxed">
              <p>
                We all thought it would clear up last night, but it has rained all day. Isabel would go out to the sheep
                this afternoon, although Dad said they will be better left alone. We have 6 lambs to feed, and they
                take one cow’s milk. Most likely some will go out, I don’t want 6 pets. Margaret hasn’t said a word
                about coming home yet, seems happy at Waiau.
              </p>
              <p>
                We took the battery down to the box for Baird to take to Leeston and he didn’t take it, so now the
                wireless is out for the election. He didn’t take my crate of eggs either, they sat there for 4 days.
              </p>
              <p>
                You might get leave now, after being ill. I think you should get 2 weeks’ we can always hope.
              </p>
              <p className="not-italic pt-2">
                Love from<br />
                Mum
              </p>
            </div>
          </div>

          {/* Image 135: Annie_s letter to Warren */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/135 Annie_s letter to Warren.jpg"
              alt="Annie's letter to Warren"
              caption="Annie's letter to Warren"
              filenameHint="135 Annie_s letter to Warren.jpg"
            />
          </div>
        </section>

        {/* Navigation Footer to Previous Chapter */}
        <div className="pt-8 border-t-2 border-[#d8ceb9] flex flex-col sm:flex-row items-center justify-between gap-4">
          <button
            onClick={() => onSelectPart && onSelectPart('part8')}
            className="w-full sm:w-auto px-5 py-3 rounded-2xl bg-[#e8dec9] hover:bg-[#d8ceb9] text-[#2c221e] font-serif font-bold text-sm transition flex items-center justify-center gap-2 cursor-pointer shadow-xs"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Part 8: Harry Denton WW2</span>
          </button>

          <button
            onClick={() => onSelectPart && onSelectPart('hub')}
            className="w-full sm:w-auto px-5 py-3 rounded-2xl bg-[#7a5b3a] hover:bg-[#5c4328] text-white font-serif font-bold text-sm transition flex items-center justify-center gap-2 cursor-pointer shadow-xs"
          >
            <Home className="w-4 h-4" />
            <span>Return to Dentons Overview</span>
          </button>
        </div>

      </article>
    </div>
  );
};
